import { createClient } from '@sanity/client';

const dataset = import.meta.env.VITE_SANITY_DATASET || 'production';

export const sanityClient = createClient({
  projectId: import.meta.env.VITE_SANITY_PROJECT_ID || 'placeholder',
  dataset,
  // Disable CDN for staging to see changes immediately
  // Use CDN for production for better performance
  useCdn: dataset === 'production',
  apiVersion: '2024-01-01', // Use current date
});

// ============================================
// CALCULATOR QUERIES
// ============================================

// Query for home watch pricing tiers (for calculator)
export const HOME_WATCH_PRICING_QUERY = `*[_type == "homeWatchPricing"] | order(order asc) {
  sqft,
  minSqft,
  maxSqft,
  weekly,
  monthly,
  order
}`;

// Query for calculator settings (singleton document)
export const CALCULATOR_SETTINGS_QUERY = `*[_type == "calculatorSettings"][0] {
  title,
  subtitle,
  sqftLabel,
  sqftPlaceholder,
  sqftHelperText,
  frequencyLabel,
  weeklyLabel,
  monthlyLabel,
  annualDiscountPercent,
  showAnnualPricing,
  annualPricingTitle,
  maxSqftForCalculator,
  customPricingMessage,
  footerTitle,
  footerItems,
  copyQuoteButtonText,
  copiedMessage
}`;

// ============================================
// LEGACY QUERIES (for backward compatibility)
// ============================================

// DEPRECATED: Use servicePlan schema instead of conciergeService
// Old CONCIERGE_SERVICES_QUERY removed

// Query for exterior checklist (legacy)
export const EXTERIOR_CHECKLIST_QUERY = `*[_type == "exteriorChecklist"] | order(order asc) {
  item
}`;

// Query for interior checklist (legacy)
export const INTERIOR_CHECKLIST_QUERY = `*[_type == "interiorChecklist"] | order(order asc) {
  item
}`;

// ============================================
// BLOG QUERIES
// ============================================

// Query for published blog posts
export const BLOG_POSTS_QUERY = `*[_type == "blogPost" && status == "published"] | order(publishedAt desc) {
  _id,
  title,
  slug,
  excerpt,
  publishedAt,
  featured,
  "author": author->{
    name,
    slug,
    photo
  },
  "category": category->{
    title,
    slug,
    color
  },
  featuredImage{
    asset->{
      _id,
      url
    },
    alt,
    caption
  },
  tags
}`;

// Query for a single blog post by slug
export const BLOG_POST_BY_SLUG_QUERY = `*[_type == "blogPost" && slug.current == $slug][0] {
  _id,
  title,
  slug,
  excerpt,
  content,
  publishedAt,
  "author": author->{
    name,
    slug,
    photo,
    bio,
    socialLinks
  },
  "category": category->{
    title,
    slug,
    color
  },
  featuredImage{
    asset->{
      _id,
      url
    },
    alt,
    caption
  },
  tags,
  seo
}`;

// Query for featured blog posts
export const FEATURED_BLOG_POSTS_QUERY = `*[_type == "blogPost" && status == "published" && featured == true] | order(publishedAt desc) [0...3] {
  _id,
  title,
  slug,
  excerpt,
  publishedAt,
  "author": author->{
    name,
    slug
  },
  "category": category->{
    title,
    slug,
    color
  },
  featuredImage{
    asset->{
      _id,
      url
    },
    alt
  }
}`;

// Query for blog categories
export const BLOG_CATEGORIES_QUERY = `*[_type == "blogCategory"] | order(title asc) {
  _id,
  title,
  slug,
  description,
  color
}`;

// ============================================
// PAGE QUERIES
// ============================================

// Query for a page by slug
export const PAGE_BY_SLUG_QUERY = `*[_type == "page" && slug.current == $slug][0] {
  _id,
  title,
  slug,
  pageType,
  hero{
    headline,
    subheadline,
    backgroundImage{
      asset->{
        _id,
        url
      },
      alt
    },
    ctaText,
    ctaLink
  },
  introduction{
    heading,
    content,
    image{
      asset->{
        _id,
        url
      },
      alt
    }
  },
  features{
    heading,
    "items": items[]->{
      _id,
      title,
      description,
      icon,
      image{
        asset->{
          _id,
          url
        },
        alt
      }
    }
  },
  pricingSection{
    heading,
    description,
    "plans": plans[]->{
      _id,
      name,
      serviceType,
      description,
      price,
      priceLabel,
      alternativePricing,
      features,
      highlighted,
      highlightLabel,
      order
    } | order(order asc)
  },
  teamSection{
    heading,
    "members": members[]->{
      _id,
      name,
      role,
      photo{
        asset->{
          _id,
          url
        }
      },
      bio,
      email,
      phone,
      socialLinks,
      order
    } | order(order asc)
  },
  testimonialsSection{
    heading,
    "testimonials": testimonials[]->{
      _id,
      clientName,
      clientTitle,
      clientPhoto{
        asset->{
          _id,
          url
        }
      },
      quote,
      rating,
      serviceUsed
    }
  },
  faqSection{
    heading,
    "faqs": faqs[]->{
      _id,
      question,
      answer,
      category,
      order
    } | order(order asc)
  },
  expensesNote{
    heading,
    content
  },
  customSections[]{
    heading,
    content
  },
  seo
}`;

// Query for navigation pages
export const NAVIGATION_PAGES_QUERY = `*[_type == "page" && showInNavigation == true] | order(navigationOrder asc) {
  _id,
  title,
  slug,
  navigationOrder
}`;

// ============================================
// PRICING QUERIES
// ============================================

// Query for pricing plans by service type
export const PRICING_PLANS_BY_SERVICE_QUERY = `*[_type == "pricingPlan" && serviceType == $serviceType] | order(order asc) {
  _id,
  name,
  serviceType,
  description,
  price,
  priceLabel,
  alternativePricing,
  features,
  highlighted,
  highlightLabel,
  order
}`;

// Query for all pricing plans
export const ALL_PRICING_PLANS_QUERY = `*[_type == "pricingPlan"] | order(serviceType asc, order asc) {
  _id,
  name,
  serviceType,
  description,
  price,
  priceLabel,
  alternativePricing,
  features,
  highlighted,
  highlightLabel,
  order
}`;

// ============================================
// TESTIMONIAL QUERIES
// ============================================

// Query for featured testimonials
export const FEATURED_TESTIMONIALS_QUERY = `*[_type == "testimonial" && featured == true] {
  _id,
  clientName,
  clientTitle,
  clientPhoto{
    asset->{
      _id,
      url
    }
  },
  quote,
  rating,
  serviceUsed,
  date
}`;

// Query for all testimonials
export const ALL_TESTIMONIALS_QUERY = `*[_type == "testimonial"] | order(date desc) {
  _id,
  clientName,
  clientTitle,
  clientPhoto{
    asset->{
      _id,
      url
    }
  },
  quote,
  rating,
  serviceUsed,
  date
}`;

// ============================================
// FAQ QUERIES
// ============================================

// Query for FAQs by category
export const FAQS_BY_CATEGORY_QUERY = `*[_type == "faq" && category == $category] | order(order asc) {
  _id,
  question,
  answer,
  category,
  order
}`;

// Query for all FAQs
export const ALL_FAQS_QUERY = `*[_type == "faq"] | order(category asc, order asc) {
  _id,
  question,
  answer,
  category,
  order
}`;

// ============================================
// TEAM QUERIES
// ============================================

// Query for active team members
export const TEAM_MEMBERS_QUERY = `*[_type == "teamMember" && active == true] | order(order asc) {
  _id,
  name,
  role,
  photo{
    asset->{
      _id,
      url
    }
  },
  bio,
  email,
  phone,
  socialLinks,
  order
}`;

// ============================================
// HOMEPAGE SETTINGS QUERY
// ============================================

// Query for homepage settings (singleton document)
export const HOMEPAGE_SETTINGS_QUERY = `*[_type == "homepageSettings"][0] {
  hero{
    headline,
    subheadline,
    backgroundImage{
      asset->{
        _id,
        url
      },
      alt
    },
    ctaPrimaryText,
    ctaPrimaryLink,
    ctaSecondaryText,
    ctaSecondaryLink
  },
  trustBadges{
    enabled,
    badges[]{
      icon,
      number,
      label
    }
  },
  whyChooseUs{
    enabled,
    heading,
    subheading,
    "features": features[]->{
      _id,
      title,
      description,
      icon,
      image{
        asset->{
          _id,
          url
        },
        alt
      }
    }
  },
  testimonials{
    enabled,
    heading,
    subheading,
    "items": items[]->{
      _id,
      clientName,
      clientTitle,
      clientPhoto{
        asset->{
          _id,
          url
        }
      },
      quote,
      rating,
      serviceUsed,
      date
    }
  },
  servicesPreview{
    enabled,
    heading,
    subheading,
    homeWatch{
      title,
      description,
      image{
        asset->{
          _id,
          url
        },
        alt
      },
      link
    },
    concierge{
      title,
      description,
      image{
        asset->{
          _id,
          url
        },
        alt
      },
      link
    }
  },
  ctaSection{
    enabled,
    heading,
    subheading,
    backgroundImage{
      asset->{
        _id,
        url
      },
      alt
    },
    buttonText,
    buttonLink,
    trustIndicators[]{
      icon,
      text
    }
  },
  seo{
    metaTitle,
    metaDescription,
    keywords
  }
}`;
